import { H2, Id, Loading, isSuccess, useToasts } from "@dgs/core";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { meService, useMyPreferencesQuery } from "../../shared/api";
import { meKeys } from "../../shared/api/me/meKeys";
import { Preference } from "../../shared/types";
import { MyPreferencesForm } from "./MyPreferencesForm";

const Container = styled.div`
	display: grid;
	grid-auto-rows: max-content;
	gap: ${({ theme }) => theme.spacing(3)};
	margin-top: ${({ theme }) => theme.spacing(5)};
`;

const Description = styled.p`
	font-size: 18px;
	max-width: 900px;
`;

export const MyPreferences: FC<{ preferences: Preference[] }> = ({ preferences }) => {
	const { t } = useTranslation();
	const { showToast } = useToasts();
	const { data: myPreferences, isLoading } = useMyPreferencesQuery();
	const queryClient = useQueryClient();

	const submitMyPreferences = useCallback(
		async (values: { preferenceIds: Id[] }) => {
			const response = await meService.updateMyPreferences(values.preferenceIds);

			if (isSuccess(response)) {
				await queryClient.invalidateQueries({ queryKey: meKeys.preferences });
				showToast({ body: t("Your preferences have been saved."), type: "success", title: t("Preferences") });
			}
		},
		[showToast, t, queryClient],
	);

	return (
		<Container>
			<H2>{t("Did you know that you can personalize your experience?")}</H2>
			<Description>
				{t(
					"Select your preferred topics below to get personalized recommendations for products, news and videos. You may change your preferences anytime by editing your profile.",
				)}
			</Description>
			{isLoading ? (
				<Loading />
			) : (
				<MyPreferencesForm preferences={preferences} myPreferences={myPreferences} onSubmit={submitMyPreferences} />
			)}
		</Container>
	);
};
